// „Für die Dachpartie“ an der Station Dach (E3c, S5; ANFORDERUNGEN.md H32):
// Höhe bis zur Dachrinne (ungefähr, in Metern), der Zugang (Leiter reicht /
// Gerüst nötig / Hubsteiger nötig / weiß nicht) und ein Satz für die
// Dachpartie. Freiwillig, nichts muss – die Angaben gehen mit der Dach-
// Unterlage an die Dachpartie (CRM, Karte „Dachpartie“).
//
// Im Stil der Station: die Höhe als Feld wie eine Länge, der Zugang als
// Auswahl zum Antippen (noch einmal antippen nimmt ihn weg), der Satz als
// Feld. Gespeichert wird beim Tippen (kurz entprellt) und beim Antippen
// sofort – auch ohne Empfang. Im Nachtrag lassen sich die Angaben
// berichtigen wie „Sonstiges“ (Prüfung des Plans E3c, Befund 10); gesendet
// stehen sie zum Lesen da.

import { useState } from "react";
import { DACH_GRENZEN, DACH_ZUGAENGE, DACH_ZUGANG_NAMEN, dachFaktenDa, dachFaktenZeile, type DachFakten } from "@happy-energy/kern";
import { dachFaktenSpeichern, type Bearbeiten } from "../arbeitskopie.ts";
import { dachAngabe, dachEingabeAus, type DachEingabe } from "../dach.ts";
import type { Station } from "../speicher.ts";
import { Gespeichert, LeseText, useSpeichern } from "./gemeinsam.tsx";

/** Die Marke am Titel – wie bei „Sonstiges“: im Nachtrag „berichtigt“, beim Nachsehen ebenso. */
function dachMarke(station: Pick<Station, "geaendertImNachtrag">, modus: Bearbeiten): string | null {
  if (modus.art === "nachtrag") return station.geaendertImNachtrag === modus.nachtragId ? "berichtigt" : null;
  return modus.art === "gesperrt" && station.geaendertImNachtrag ? "berichtigt" : null;
}

export function DachFelder({ station, modus }: { station: Station; modus: Bearbeiten }) {
  const nurLesen = modus.art === "gesperrt";
  const f = station.fachfragen ?? null;
  const marke = dachMarke(station, modus);
  const [eingabe, setEingabe] = useState<DachEingabe>(() => dachEingabeAus(f));
  const [gespeichert, setGespeichert] = useState(false);
  const speichern = useSpeichern(async (fakten: DachFakten) => {
    await dachFaktenSpeichern(station, fakten);
    setGespeichert(true);
  });
  const geprueft = dachAngabe(eingabe);

  /** Neue Eingabe: merken und – wenn sie passt – speichern (beim Antippen gleich, beim Tippen nach einer kurzen Pause). */
  function aendern(neu: DachEingabe, gleich = false) {
    setEingabe(neu);
    const g = dachAngabe(neu);
    if (!g.ok) {
      speichern.verwerfen();
      return;
    }
    speichern.spaeter(g.fakten);
    if (gleich) speichern.sofort();
  }

  const titel = (
    <div className="abschnitt-zeile">
      <div className="abschnitt-titel" id="dach-titel">
        Für die Dachpartie
      </div>
      {marke && <span className="sendungs-marke">{marke}</span>}
    </div>
  );

  if (nurLesen) {
    return (
      <>
        {titel}
        <div className="text-karte nur-lesen" aria-labelledby="dach-titel" role="group">
          {dachFaktenDa(f) ? (
            <>
              <p className="lese-text">{dachFaktenZeile(f)}</p>
              {f.hinweis && <LeseText text={f.hinweis} leer="" />}
            </>
          ) : (
            <LeseText text={null} leer="Höhe und Zugang nicht angegeben." />
          )}
        </div>
      </>
    );
  }

  return (
    <>
      {titel}
      <div className="dach-felder" role="group" aria-labelledby="dach-titel">
        <p className="klein-text">Freiwillig – sagt der Dachpartie, welche Leiter oder ob ein Gerüst nötig ist.</p>
        <label className={`laenge-feld dach-hoehe${geprueft.ok ? "" : " ungueltig"}`} htmlFor="dach-hoehe">
          <span className="laenge-name">Höhe bis zur Dachrinne</span>
          <span className="laenge-wert">
            <span className="einheit">ca.</span>
            <input
              id="dach-hoehe"
              inputMode="decimal"
              enterKeyHint="done"
              autoComplete="off"
              placeholder="–"
              maxLength={8}
              value={eingabe.hoehe}
              aria-invalid={geprueft.ok ? undefined : true}
              onChange={(e) => aendern({ ...eingabe, hoehe: e.target.value })}
              onBlur={speichern.sofort}
            />
            <span className="einheit">m</span>
          </span>
        </label>
        <div className="dach-zugang">
          <span className="laenge-name" id="dach-zugang-titel">
            Zugang
          </span>
          <div className="auswahl" role="group" aria-labelledby="dach-zugang-titel">
            {DACH_ZUGAENGE.map((z) => (
              <button
                key={z}
                type="button"
                className={eingabe.zugang === z ? "an" : undefined}
                aria-pressed={eingabe.zugang === z}
                onClick={() => aendern({ ...eingabe, zugang: eingabe.zugang === z ? null : z }, true)}
              >
                {DACH_ZUGANG_NAMEN[z]}
              </button>
            ))}
          </div>
        </div>
        <div className="feld">
          <label htmlFor="dach-hinweis">Ein Satz für die Dachpartie</label>
          <input
            id="dach-hinweis"
            autoComplete="off"
            maxLength={DACH_GRENZEN.hinweis}
            placeholder="z. B. Zufahrt eng, Stromleitung vor dem Haus"
            value={eingabe.hinweis}
            onChange={(e) => aendern({ ...eingabe, hinweis: e.target.value })}
            onBlur={speichern.sofort}
          />
        </div>
        {!geprueft.ok ? (
          <div className="feld-hinweis">{geprueft.meldung}</div>
        ) : (
          gespeichert && (
            <div className="klein-text">
              <Gespeichert an />
              {modus.art === "nachtrag" ? " · Berichtigt kommt im Büro als neu an." : ""}
            </div>
          )
        )}
      </div>
    </>
  );
}
